/**
 * Gaushala API Service
 * Story 2-4: Gaushala Selector Component (Frontend)
 *
 * Provides API methods for fetching gaushala data from the gaushala-service
 *
 * Service URLs come from serviceConfig.ts (single source of truth).
 * To change a service URL/port, update ONLY the .env file.
 */

import { getServiceUrl } from '../../config/serviceConfig';

/**
 * Gaushala API response structure
 */
export interface GaushalaDTO {
  id: number;
  name: string;
  registrationNumber: string;
  address: string;
  district: string;
  area: string;
  pincode: string;
  state?: string;
  contactNumber?: string;
  email?: string;
  capacity?: number;
  establishedDate?: string;
  status?: string;
}

/**
 * Get the gaushala service base URL from serviceConfig (single source of truth)
 */
function getGaushalaServiceUrl(): string {
  return getServiceUrl('gaushala');
}

/**
 * Get authentication token from localStorage
 */
function getAuthToken(): string | null {
  const token = localStorage.getItem('saubhagya_jwt_token') || localStorage.getItem('token');
  if (token) {
    console.log('[GaushalaApi] Token retrieved:', {
      type: typeof token,
      length: token.length,
      startsWithBearer: token.startsWith('Bearer '),
      first30Chars: token.substring(0, 30),
      hasComma: token.includes(','),
    });
  }
  return token;
}

/**
 * Create headers with authentication
 */
function createHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const token = getAuthToken();
  if (token) {
    const cleanToken = token.startsWith('Bearer ') ? token.substring(7) : token;
    headers['Authorization'] = `Bearer ${cleanToken}`;
    console.log('[GaushalaApi] Authorization header created:', {
      tokenLength: cleanToken.length,
      headerStart: (headers['Authorization'] as string).substring(0, 30),
    });
  }

  return headers;
}

/**
 * Gaushala API service
 */
export const gaushalaApi = {
  /**
   * Fetch all gaushalas from the admin endpoint
   * Endpoint: GET /api/v1/gaushala/admin/gaushalas
   *
   * @returns Promise<GaushalaDTO[]> - Array of gaushalas
   * @throws Error if the API call fails
   */
  async getGaushalas(): Promise<GaushalaDTO[]> {
    const baseUrl = getGaushalaServiceUrl();
    const url = `${baseUrl}/api/v1/gaushala/admin/gaushalas`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch gaushalas: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const data = await response.json();

      // Backend returns: { success: true, gaushalas: [...] }
      // Extract the gaushalas array
      if (data.success && Array.isArray(data.gaushalas)) {
        return data.gaushalas;
      }

      // Fallback: check if data itself is an array
      if (Array.isArray(data)) {
        return data;
      }

      // Fallback: check if data.data exists
      if (data.data && Array.isArray(data.data)) {
        return data.data;
      }

      console.warn('Unexpected gaushala API response format:', data);
      return [];
    } catch (error) {
      console.error('Error fetching gaushalas:', error);
      throw error;
    }
  },

  /**
   * Fetch gaushala by ID
   * Endpoint: GET /api/v1/gaushala/admin/gaushalas/{id}
   *
   * @param id - Gaushala ID
   * @returns Promise<GaushalaDTO> - Gaushala details
   * @throws Error if the API call fails
   */
  async getGaushalaById(id: number): Promise<GaushalaDTO> {
    const baseUrl = getGaushalaServiceUrl();
    const url = `${baseUrl}/api/v1/gaushala/admin/gaushalas/${id}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch gaushala: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const data = await response.json();

      // Backend might return: { success: true, gaushala: {...} } or direct object
      if (data.success && data.gaushala) {
        return data.gaushala;
      }

      if (data.data) {
        return data.data;
      }

      return data;
    } catch (error) {
      console.error(`Error fetching gaushala ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get current user's gaushala profile
   * Endpoint: GET /api/v1/gaushala/profile
   *
   * @returns Promise<GaushalaDTO> - Current user's gaushala
   * @throws Error if the API call fails
   */
  async getCurrentGaushala(): Promise<GaushalaDTO> {
    const baseUrl = getGaushalaServiceUrl();
    const url = `${baseUrl}/api/v1/gaushala/profile`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch current gaushala: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const data = await response.json();

      // Backend returns: { gaushala: {...}, ... }
      if (data.gaushala) {
        return data.gaushala;
      }

      if (data.data) {
        return data.data;
      }

      return data;
    } catch (error) {
      console.error('Error fetching current gaushala:', error);
      throw error;
    }
  },
};
